import React, { ReactElement, FunctionComponent } from 'react';
import styled from 'styled-components';
import { useMedia } from 'react-media-match';
import { Increase, Decrease } from '@/src/components/icons';
import { useBasket } from '@/src/components/ProductContext';

const InputWrapper = styled.div<{ mobile: boolean }>`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  ${({ mobile }) => mobile && 'margin: 0 0.75rem'}
`;

const QuantityInput = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  border: 2px solid #dcdcdc;
  font-size: 1rem;
  margin: 0 0.75rem;
  pointer-events: none;
  width: 4rem;
  height: 2rem;
  user-select: none;
`;

const QuantityButton = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  background: #f4f4f4;
  cursor: pointer;
  width: 2.25rem;
  height: 2rem;
  user-select: none;
`;

interface QuantitySelectionProps {
  /**
   * Sets the step how the quantity will be increased or decreased on button click.
   * @default 1
   */
  step?: number;
  /**
   * Sets the minimum value for the quantity selection.
   * @default 1
   */
  min?: number;
  /**
   * Sets the maximum value for the quantity selection.
   * @default 10
   */
  max?: number;
}

/**
 * This component renders the quantity selection. If the current product has a quantity selection available
 * this component will render an increase and decrease button. If the quantity changes the component updates
 * the quantity value withtin the product context.
 */
export const QuantitySelection: FunctionComponent<QuantitySelectionProps> = ({
  step = 1,
  min = 1,
  max = 10,
}): ReactElement => {
  const isMobile = useMedia({ desktop: false, mobile: true, tablet: false });
  const { quantity, setQuantity } = useBasket();

  return (
    <InputWrapper mobile={isMobile} data-testid="quantity-selection">
      <QuantityButton
        onClick={() => quantity > min && setQuantity(quantity - step)}
        data-testid="quantity-decrease"
      >
        <Decrease fill="#444444" />
      </QuantityButton>
      <QuantityInput data-testid="quantity-value">{quantity}</QuantityInput>
      <QuantityButton
        onClick={() => quantity < max && setQuantity(quantity + step)}
        data-testid="quantity-increase"
      >
        <Increase fill="#444444" />
      </QuantityButton>
    </InputWrapper>
  );
};

export default QuantitySelection;
